<template>
  <v-card id="rate-sheet-viewer">
    <v-card-title class="primary white--text">
      <v-row align="center">
        <v-col>
          <span class="headline">
            {{ componentTitle }}
          </span>
        </v-col>

        <slot name="prepend-dialog-actions"/>

        <v-col cols="auto" v-if="initialized">
          <BaseDialogActions hideRefresh elementId="rate-sheet-viewer-actions"/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-5">
      <slot name="card-body-prepend"/>
      <v-row justify="end" align="center">
        <v-col cols="12" lg="3" xl="2" v-if="showRateSheetVersionSelector">
          <LoadableComponent :loading="loading || !initialized">
            <v-card outlined>
              <RateSheetVersionSelector
                :disabled="false"
                :items="rateSheetVersionOptions"
                @chosen="rateSheetVersionChosen"/>
            </v-card>
          </LoadableComponent>
        </v-col>

        <v-col cols="12" v-if="lineItems">
          <LoadableComponent :loading="loading || !initialized">
            <RateSheetLineTableFilterable
              :rateSheetInteractionMode="RateSheetInteractionMode.RateSheet.View"
              :newLineDisabled="true"
              :lines="lineItems"
              :accountId="accountId"
              :loading="loading"
              :itemClass="getClassForRow"
              :tableHeightCalculationData="tableHeightCalculationData"
              :triggerFadeTranslateDown="triggerRateTableFade"
              :expansion="expansionDataForRateSheetLineTable"
              :propShowExpansionIcon="allowExpansion"
              :baseRateCostCalculationId="activity?.baseCost?.costCalculationId"
              @expansion-clicked="expansionClicked"
              @expansion-info-clicked="expansionInfoClicked"
              @group-by-species-group-toggled="groupBySpeciesGroupToggled"
              @expansion-filtered="expansionFiltered"
            />
          </LoadableComponent>
        </v-col>
      </v-row>
      <slot name="card-body-append"/>
    </v-card-text>

    <Dialog :stateId="dialogId" @close="closeDialog" @click:outside="closeDialog" :fullscreenBreakPoint="'xs'">
      <ExpansionDetails :expansion="expansionDataForExpansionDetails" v-if="viewingExpansionDetails" />
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ContractMode } from '@/utils/Enumerations'
import { cloneDeep } from 'lodash'
import { rateSheetDefinitionLineSorter } from '@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitions'
import { uniqueDialogId } from '@/utils/componentHelpers'
import moment from 'moment'
import { getLengthGroupTooltip } from '@/components/account/account-scaling/length-groups/LengthGroups'
import { getSpeciesGroupTooltip } from '@/components/account/account-scaling/species-groups/SpeciesGroups'
import { getDiameterGroupTooltip } from '@/components/account/account-scaling/diameter-groups/DiameterGroups'
import { RateSheetInteractionMode } from '@/components/activity/rate-sheet/RateSheet'

export default {
  name: 'RateSheetViewer',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    RateSheetVersionSelector: () => import('@/components/activity/rate-sheet/RateSheetVersionSelector.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    RateSheetLineTableFilterable: () => import('@/components/activity/rate-sheet/RateSheetLineTableFilterable.vue'),
    ExpansionDetails: () => import('@/components/activity/rate-sheet/ExpansionDetails.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  props: {
    accountId: {
      type: Number,
      required: true
    },
    rateSheet: {
      type: Object,
      required: true
    },
    emphasizedLineId: {
      type: Number,
      required: false,
      default: null
    },
    tableHeightCalculationData: {
      type: Object,
      required: false,
      default: () => ({ auto: true })
    },
    activity: {
      type: Object,
      required: false,
      default: undefined
    },
    contract: {
      type: Object,
      required: false,
      default: undefined
    },
    selectedDate: {
      type: String,
      default: null
    }
  },

  data: () => ({
    currentVersion: null,
    loading: false,
    dialogId: uniqueDialogId('rate-sheet-viewer'),
    initialized: false,
    groupBySpeciesGroup: false,
    rateSheetVersionOptions: [],
    triggerRateTableFade: false,
    expansionVisible: false,
    expansionData: null,
    viewingExpansionDetails: false,
    expansionProduct: null,
    ContractMode,
    RateSheetInteractionMode
  }),

  computed: {
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('account-scaling', ['accountLengthGroups', 'accountIdOfLengthGroups',
      'accountDiameterGroups', 'accountIdOfDiameterGroups',
      'accountSpeciesGroups']),

    lineItems () {
      if (!this.currentVersion) return []
      return this.currentVersion.rateSheetLines?.toSorted(rateSheetDefinitionLineSorter) ?? []
    },

    componentTitle () {
      let title = this.rateSheet?.name ??
          this.currentVersion?.name ??
          this.$t('rateSheet')

      if (this.activity?.activityTemplate?.name) {
        title = `${title} (${this.activity.activityTemplate.name})`
      }

      return title
    },

    showRateSheetVersionSelector () {
      return this.rateSheetVersionOptions?.length > 1
    },

    expansionDataForRateSheetLineTable () {
      return this.expansionVisible ? this.expansionData : null
    },

    expansionDataForExpansionDetails () {
      if (!this.expansionData) return null
      if (this.expansionData.length === 1) {
        return this.expansionData[0]
      }
      return this.expansionData.find(exp => exp.productId === this.expansionProduct)
    },

    allowExpansion () {
      const hasExpansionId = this.contract?.expansionId !== null && this.contract?.expansionId !== undefined && this.contract.expansionId > 0
      return hasExpansionId
    }
  },

  async mounted () {
    this.currentVersion = cloneDeep(this.rateSheet)
    await this.initializeAccountScalingSpecs()
    this.initialized = true
  },

  methods: {
    getLengthGroupTooltip,
    getSpeciesGroupTooltip,
    getDiameterGroupTooltip,

    ...mapActions('dialog', ['closeDialogsAtOrAbove', 'openOrUpdateDialog']),
    ...mapActions('account-scaling', ['fetchAccountLengthGroups', 'fetchAccountDiameterGroups', 'fetchAccountSpeciesGroups']),
    ...mapActions('contract', ['fetchExpansionForContract']),

    async initializeAccountScalingSpecs () {
      if (this.accountIdOfLengthGroups !== this.accountId) {
        await this.fetchAccountLengthGroups(this.accountId)
      }
      if (this.accountIdOfDiameterGroups !== this.accountId) {
        await this.fetchAccountDiameterGroups(this.accountId)
      }
      if (this.accountSpeciesGroups.length === 0 || this.accountSpeciesGroups[0].accountId !== this.accountId) {
        await this.fetchAccountSpeciesGroups(this.accountId)
      }
    },

    closeDialog () {
      this.viewingExpansionDetails = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    openDialog (width = '500px') {
      this.openOrUpdateDialog({ id: this.dialogId, width })
    },

    getClassForRow (item) {
      return this.emphasizedLineId && item.rateSheetLineId === this.emphasizedLineId
        ? `rate-sheet-line-${item.rateSheetLineId} emphasized${this.groupBySpeciesGroup && this.$vuetify.breakpoint.mdAndUp ? '-v2' : ''} font-weight-bold`
        : `rate-sheet-line-${item.rateSheetLineId}`
    },

    groupBySpeciesGroupToggled (value) {
      this.groupBySpeciesGroup = value
    },

    rateSheetVersionChosen (version) {
      this.currentVersion = cloneDeep(version)
      this.triggerRateTableFade = true
      this.$nextTick(() => {
        this.triggerRateTableFade = false
      })
    },

    async expansionClicked () {
      this.expansionVisible = !this.expansionVisible
      await this.fetchExpansion()
    },

    async fetchExpansion () {
      if (!this.contract || this.expansionData != null) return
      let safeDate = this.selectedDate
      if (safeDate === 'Current' || !safeDate) {
        safeDate = moment().utc().toISOString()
      }
      this.expansionData = await this.fetchExpansionForContract({ contractId: this.contract.contractId, onDate: safeDate })
    },

    expansionInfoClicked () {
      this.viewingExpansionDetails = true
      this.openDialog()
    },

    expansionFiltered (productId) {
      this.expansionProduct = productId
    }
  }
}
</script>

<style>
#rate-sheet-viewer tr.deleted td {
  color: rgba(0, 0, 0, 0.4);
  text-decoration: line-through;
}

#rate-sheet-viewer .v-data-table:not(.v-data-table--mobile)>.v-data-table__wrapper>table>tbody>tr.emphasized td:first-child {
  border-left: 4px solid #d15f27;
  padding-left: 12px;
  border-image: linear-gradient(to right, #d15f27, #d15f2799 8px, rgba(0,0,0,0.12) 64px) 1;
}

#rate-sheet-viewer .v-data-table:not(.v-data-table--mobile)>.v-data-table__wrapper>table>tbody>tr:has(+ .emphasized) td:first-child {
  border-image: linear-gradient(to right, #d15f27, #d15f2799 8px, rgba(0,0,0,0.12) 64px) 1;
}

#rate-sheet-viewer .v-data-table:not(.v-data-table--mobile)>.v-data-table__wrapper>table>tbody>tr:nth-child(odd).emphasized-v2:not(:hover) td:first-child {
  background-image: linear-gradient(to right, #d15f2788, #f7f7f7 12px, #f7f7f7 64px);
}

#rate-sheet-viewer .v-data-table:not(.v-data-table--mobile)>.v-data-table__wrapper>table>tbody>tr:nth-child(even).emphasized-v2:not(:hover) td:first-child {
  background-image: linear-gradient(to right, #d15f2788, white 12px, white 64px);
}

#rate-sheet-viewer .v-data-table:not(.v-data-table--mobile)>.v-data-table__wrapper>table>tbody>tr.emphasized-v2:hover td:first-child {
  background-image: linear-gradient(to right, #d15f2788, #eee 12px, #eee 64px);
}

#rate-sheet-viewer .v-data-table.v-data-table--mobile>.v-data-table__wrapper>table>tbody>tr.emphasized td {
  border-left: 4px solid #d15f27;
  padding-left: 12px;
}

#rate-sheet-viewer .v-data-table.v-data-table--mobile>.v-data-table__wrapper>table>tbody>tr.emphasized td:last-child {
  border-image: linear-gradient(to right, #d15f27, #d15f2799 8px, rgba(0,0,0,0.12) 64px) 1;
}

#rate-sheet-viewer .v-data-table.v-data-table--mobile>.v-data-table__wrapper>table>tbody>tr:has(+ .emphasized) td:last-child {
  border-image: linear-gradient(to right, #d15f27, #d15f2799 8px, rgba(0,0,0,0.12) 64px) 1;
}
</style>
